import { CitationTag } from "components/CitationTag";
import PaperMetadata from "components/PaperMetadata";
import { useFullPaperLibraryLink } from "features/libraries";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper } from "helpers/api";
import { detailPageUrl } from "helpers/pageUrl";
import Link from "next/link";

/**
 * Props for the KeyPaperPreview component
 */
interface KeyPaperPreviewProps {
  /** The original index/citation number of the paper to display */
  originalIndex: string;
  /** Array of all available papers to search through for the matching paper */
  papers?: Paper[];
}

/**
 * A compact preview card component that displays key information about a research paper.
 * Used to show paper details when referenced by citation number in research content.
 *
 * Features:
 * - Shows citation number, paper title, publication year
 * - Displays citation count and primary author
 * - Shows journal name and any badges (peer review status, etc.)
 * - Links to the full paper detail page
 *
 */
const KeyPaperPreview: React.FC<KeyPaperPreviewProps> = ({
  originalIndex, // llm provides 1-indexed citation number
  papers = [],
}) => {
  const paper = papers[parseInt(originalIndex) - 1];
  const { library_id } = useUserSettings();

  // Get library link for this paper
  const { data: fullPaperLibraryLink } = useFullPaperLibraryLink(
    library_id,
    paper?.doi,
    Boolean(library_id && paper?.doi)
  );

  if (!paper) {
    console.warn(
      `KeyPaperPreview: Paper with originalIndex ${originalIndex} not found in papers array`
    );
    return null;
  }

  return (
    <span className="inline-table w-full group min-w-[var(--result-table-paper-col-width)]">
      <Link
        href={detailPageUrl(paper.url_slug, paper.paper_id)}
        target="_blank"
        className="no-underline"
      >
        <div className="flex gap-3 items-center mb-2">
          {/* Citation number */}
          <div className="w-[29px]">
            <CitationTag paperIndex={parseInt(originalIndex) - 1} />
          </div>
          {/* Paper title */}
          <h2 className="flex-1 text-fg-base sm-md not-prose line-clamp-2 group-hover:underline">
            {paper.title}
          </h2>
        </div>
        <PaperMetadata paper={paper} useTwoRowLayout />
      </Link>
    </span>
  );
};

/**
 * Converts key paper data to markdown format for plain text export
 *
 * @param props - KeyPaperPreview props containing the original index
 * @returns Simple citation number in brackets format
 */
export const KeyPaperPreviewMarkdown = ({
  originalIndex,
}: KeyPaperPreviewProps) => {
  return `[${parseInt(originalIndex)}]`;
};

export default KeyPaperPreview;
